import { RefCallback, useCallback, useEffect, useRef } from 'react';
import { scopeTab } from './scope-tab';
import { FOCUS_SELECTOR, focusable, tabbable } from './tabbable';

/**
 * Хук focus trap.
 * Взят из https://github.com/mantinedev/mantine/blob/master/packages/%40mantine/hooks/src/use-focus-trap/use-focus-trap.ts
 *
 * Ограничивает клавиатурную навигацию (Tab) внутри контейнера
 * и автоматически переводит фокус внутрь при открытии.
 *
 * Используется для:
 * - Dialog
 * - Modal
 * - Drawer
 * - Popover с управлением фокусом
 * - др.
 */
export function useFocusTrap(active = true): RefCallback<HTMLElement | null> {
  const ref = useRef<HTMLElement | null>(null);

  const focusNode = (node: HTMLElement) => {
    let focusElement: HTMLElement | null =
      node.querySelector('[data-autofocus]');

    if (!focusElement) {
      const children = Array.from<HTMLElement>(
        node.querySelectorAll(FOCUS_SELECTOR)
      );
      focusElement =
        children.find(tabbable) || children.find(focusable) || null;

      if (!focusElement && focusable(node)) {
        focusElement = node;
      }
    }

    if (focusElement) {
      focusElement.focus({ preventScroll: true });
    } else if (process.env.NODE_ENV === 'development') {
      console.warn(
        '[useFocusTrap] Не удалось найти элемент, который может получить фокус, внутри переданного контейнера',
        node
      );
    }
  };

  const setRef = useCallback(
    (node: HTMLElement | null) => {
      if (!active || node === null || ref.current === node) {
        return;
      }

      if (node) {
        setTimeout(() => {
          if (node.getRootNode()) {
            focusNode(node);
          } else if (process.env.NODE_ENV === 'development') {
            console.warn(
              '[useFocusTrap] Узел ref отсутствует в DOM-дереве',
              node
            );
          }
        });

        ref.current = node;
      } else {
        ref.current = null;
      }
    },
    [active]
  );

  useEffect(() => {
    if (!active) {
      return undefined;
    }

    ref.current && setTimeout(() => focusNode(ref.current!));

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Tab' && ref.current) {
        scopeTab(ref.current, event);
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [active]);

  return setRef;
}
